import React, { Fragment, useState } from "react";
import { ModeBadge } from "../../components/ModeBadge";
import { ChevronDown, ChevronRight, X, AlertTriangle } from "lucide-react";
import { fmt, fmtPct, pctColor, fmtTime, STRATEGY_LABELS } from "./commoditiesLib";

/**
 * 2026-08-28 — extracted from CommoditiesPaperTrading.jsx so the same
 * expandable trade row (with the "needed parameters" detail — RVOL, IV,
 * DTE, contract phase, SL/TP premium, lot size — the same class of detail
 * NSE Stocks' ExecutionJourney panel surfaces) can be reused on the P&L
 * page too, for BOTH open and closed trades, instead of duplicating this
 * table or leaving the P&L page's trade list plain. (Chat: "Hope it will
 * also Open trades, similar to Stocks with details like RVOL, needed
 * parameters")
 */
// 2026-08-28 — was an unconditional "Simulated pricing (Black-76)" line on
// every trade, even after commodity_autopilot._resolve_open_trades() was
// changed to prefer a real Upstox MCX option quote for SL/TP checks when
// one resolves (chat: "Did Crude Oil 8000PE really hot Stop Loss?"). Now
// reflects the actual per-trade pricing source: `price_source` for OPEN
// trades' current premium, `exit_pricing_source` for CLOSED trades' exit
// premium. Older trade docs predating this field default to the
// Black-76 label, since that was the only path that existed then.
// 2026-10-01 — a row with a real Kite fill shows ONLY real values (chat:
// "Just keep Live order - real values"); paper rows are priced off Kite's
// live MCX quote since the 2026-09-24 data migration (was labelled Upstox).
const pricingSourceLabel = (t) => {
  if (t.live_avg_fill_price != null) {
    return t.status === "OPEN"
      ? `Real ${t.live_broker || "Kite"} fill — entry is the actual order price; current is the live Kite quote.`
      : `Real ${t.live_broker || "Kite"} fills — entry, exit and P&L are the actual order prices.`;
  }
  const source = t.status === "OPEN" ? t.price_source : t.exit_pricing_source;
  if (source === "live_quote") {
    return "Live MCX option quote (Kite) — real market premium.";
  }
  return "Simulated pricing (Black-76) — no real MCX option-chain quote resolved for this trade.";
};

const TradeDetailRow = ({ t, colSpan }) => (
  <tr className="border-b border-grid/40" style={{ background: "rgba(255,255,255,0.015)" }}>
    <td colSpan={colSpan} className="px-3 py-2.5">
      <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono-data text-[11px] text-muted-2 pl-9">
        <span>SL premium <span className="text-muted-1">{fmt(t.sl_premium)}</span></span>
        <span>TP premium <span className="text-muted-1">{fmt(t.tp_premium)}</span></span>
        {/* 2026-10-01 — Kite's MCX lot_size is 1 (orders in lots); show the
            real contract size the rupee figures are computed on. */}
        <span>Qty <span className="text-muted-1">
          {(t.live_qty_lots || 1)} lot{(t.live_qty_lots || 1) > 1 ? "s" : ""}
          {t.units_per_lot ? ` × ${t.units_per_lot} units` : ""}
        </span></span>
        <span>RVOL @ entry <span className="text-muted-1">{t.rvol_at_entry != null ? `${fmt(t.rvol_at_entry, 2)}x` : "—"}</span></span>
        <span>IV @ entry <span className="text-muted-1">{t.iv_at_entry != null ? `${fmt(t.iv_at_entry * 100, 0)}%` : "—"}</span></span>
        <span>DTE @ entry <span className="text-muted-1">{t.dte_days_at_entry != null ? `${fmt(t.dte_days_at_entry, 0)}d` : "—"}</span></span>
        <span>Contract phase <span className="text-muted-1">{t.contract_phase || "—"}</span></span>
        <span>Expiry <span className="text-muted-1">{t.expiry || "—"}</span></span>
        {t.status === "CLOSED" && (
          <>
            {/* 2026-09-02 — dropped "Close reason" (already shown in the row's
                Action column) and "Exit @ time" (exit premium already shown in
                the row's Exit column, exit time now has its own Exited column)
                — both were pure duplicates of info already visible without
                expanding. Chat: "Close reason (seems to be repeated)". */}
            <span>Charges <span className="text-muted-1">{fmt(t.charges_total)}</span></span>
            <span>Net P&L <span style={{ color: pctColor(t.net_pnl) }}>{fmt(t.net_pnl)}</span></span>
          </>
        )}
        <span className="text-muted-3">{pricingSourceLabel(t)}</span>
      </div>
      {/* 2026-10-01 (chat: "Just keep Live order - real values. Even in
          Paper trading page also") — the "Real fill — shown above / simulated
          entry → sim exit / sim P&L" comparison line is gone; a live row shows
          real values only. Only an unconfirmed real exit is still called out. */}
      {t.live_avg_fill_price != null && t.status === "CLOSED" && t.live_exit_avg_fill_price == null && t.live_exit_status && (
        <div className="font-mono-data text-[11px] pl-9 pt-1 text-muted-3">
          real exit {t.live_exit_status}
        </div>
      )}
    </td>
  </tr>
);

// Small exchange badge next to the underlying name. Was originally added
// (2026-09-03) to tell MCX and NSCOM trades apart on this shared page;
// NSCOM support was removed 2026-09-22 (it never had a live option market
// and was superseded by Kite Connect for real MCX order placement), so
// this now always renders "MCX" — kept simple rather than removed
// entirely so any old doc predating the `exchange` field still displays
// sensibly (defaults to MCX, same convention as pricingSourceLabel above).
const ExchangeBadge = ({ exchange }) => (
  <span
    className="px-1.5 py-0.5 text-[9px] font-mono-data uppercase tracking-wider border rounded-sm"
    style={{ color: "var(--muted-2, #888)", borderColor: "var(--grid)" }}
    title="Multi Commodity Exchange"
  >
    {exchange || "MCX"}
  </span>
);

const TradeRow = ({ t, onExit, colSpan }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr className="border-b border-grid/40 hover:bg-white/[0.02]" data-testid="commodities-trade-row">
        <td className="px-3 py-2.5">
          <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2">
            <span className="text-muted-3">{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
            <span className="font-semibold">{t.underlying}</span>
            <ExchangeBadge exchange={t.exchange} />
            <ModeBadge t={t} className="" />
          </button>
        </td>
        <td className="px-3 py-2.5 text-muted-2">{STRATEGY_LABELS[t.signal_type] || t.signal_type}</td>
        <td className="px-3 py-2.5" style={{ color: t.direction === "Bullish" ? "var(--bull)" : "var(--bear)" }}>
          {t.direction}
        </td>
        <td className="px-3 py-2.5 text-muted-2">{t.option_type} {fmt(t.strike, 0)}</td>
        {/* 2026-09-24 (chat: "Let's bring actual values and display them in
            Entry and Exit") — entry_premium/exit_premium are now the REAL
            Kite fill whenever one exists (commodity_autopilot._use_real_pricing),
            simulated-only otherwise. The dot flags which one is being shown
            here at a glance, since both this screen (mixed real+simulated
            rows) and the P&L page (all-real rows) share this component. */}
        <td className="px-3 py-2.5 text-right text-muted-2">
          {t.live_avg_fill_price != null && (
            <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: "var(--accent, #a78bfa)" }} title={`Real ${t.live_broker || "Kite"} fill`} />
          )}
          {fmt(t.entry_premium)}
        </td>
        <td className="px-3 py-2.5 text-right text-muted-2">
          {t.status === "CLOSED" && t.real_pnl_confirmed === false ? (
            <span className="text-[10px] text-muted-3 uppercase" title={t.live_exit_status || "real exit not confirmed"}>
              Unconfirmed
            </span>
          ) : (
            <>
              {t.status === "CLOSED" && t.live_exit_avg_fill_price != null && (
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: "var(--accent, #a78bfa)" }} title={`Real ${t.live_broker || "Kite"} fill`} />
              )}
              {fmt(t.status === "OPEN" ? t.current_premium : t.exit_premium)}
            </>
          )}
        </td>
        <td className="px-3 py-2.5 text-right font-semibold" style={{ color: pctColor(t.pnl_pct) }}>
          {/* 2026-09-16 — real_pnl_confirmed=false (set by real_pnl_summary
              on the P&L page only) means this real trade closed WITHOUT a
              confirmed real exit fill — pnl_pct is deliberately null here
              rather than a possibly-wrong number, so show that explicitly
              instead of a bare "—" that looks like ordinary missing data.
              Chat: "Deployed..PNL is still showing as Open" (the trade that
              prompted this used to vanish from the page entirely). */}
          {t.real_pnl_confirmed === false ? (
            <span className="text-[10px] text-muted-3 uppercase" title={t.live_exit_status || "exit not confirmed"}>
              Unconfirmed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 justify-end">
              {/* 2026-09-22 (chat: "why are we still using Black-Scholes...
                  doesn't match with live prices" -> "flag it") — entry and
                  current/exit pricing are resolved independently and can
                  disagree (one Black-76, one a real MCX quote); this % then
                  partly reflects that switch, not just real price movement.
                  See commodity_autopilot._serialize's pricing_basis_mismatch
                  comment for the full mechanism. */}
              {t.pricing_basis_mismatch && (
                <AlertTriangle
                  size={11}
                  style={{ color: "#FFB66E" }}
                  title={t.pricing_basis_note || "Entry and current/exit pricing used different sources (Black-76 vs a real MCX quote) — this % isn't purely real price movement."}
                />
              )}
              {fmtPct(t.pnl_pct)}
            </span>
          )}
        </td>
        <td className="px-3 py-2.5 text-muted-3 font-mono-data text-[10px]">{fmtTime(t.entry_time)}</td>
        <td className="px-3 py-2.5 text-muted-3 font-mono-data text-[10px]">
          {t.status === "CLOSED" ? fmtTime(t.exit_time) : "—"}
        </td>
        <td className="px-3 py-2.5 text-right">
          {t.status === "OPEN" ? (
            onExit ? (
              <button
                onClick={() => onExit(t)}
                data-testid={`commodities-manual-exit-${t.id}`}
                className="px-2 py-1 border text-[10px] uppercase tracking-wider font-mono-data flex items-center gap-1"
                style={{ color: "var(--bear)", borderColor: "var(--bear)" }}
              >
                <X size={10} /> Exit
              </button>
            ) : (
              <span className="text-[10px] text-muted-3 font-mono-data uppercase">Open</span>
            )
          ) : (
            <span className="text-[10px] text-muted-3 font-mono-data uppercase">{t.close_reason || t.outcome}</span>
          )}
        </td>
      </tr>
      {open && <TradeDetailRow t={t} colSpan={colSpan} />}
    </Fragment>
  );
};

export const CommodityTradeTable = ({ trades, onExit, emptyLabel, testid }) => {
  const colSpan = 10;
  return (
    <div className="border border-grid overflow-x-auto" data-testid={testid}>
      {!trades || trades.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted-2 font-mono-data">
          {emptyLabel || "No trades."}
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
              <th className="px-3 py-2">Underlying</th>
              <th className="px-3 py-2">Strategy</th>
              <th className="px-3 py-2">Direction</th>
              <th className="px-3 py-2">Leg</th>
              <th className="px-3 py-2 text-right">Entry</th>
              <th className="px-3 py-2 text-right">{trades[0]?.status === "OPEN" ? "Current" : "Exit"}</th>
              <th className="px-3 py-2 text-right">P&amp;L%</th>
              <th className="px-3 py-2">Entered</th>
              <th className="px-3 py-2">Exited</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => (
              <TradeRow key={t.id} t={t} onExit={onExit} colSpan={colSpan} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CommodityTradeTable;
